import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import SwirlDashboard from "./pages/SwirlDashboard";
import { RecommendationsPage } from "./pages/RecommendationsPage";
import DiscoverPage from "./pages/DiscoverPage";
import DataHealthDashboard from "./pages/debug/DataHealthDashboard";
import ChannelsPage from "./pages/ChannelsPage";
import CollectionsPage from "./pages/CollectionsPage";
import PeoplePage from "./pages/PeoplePage";
import CreativeLabPage from "./pages/CreativeLabPage";
import AIChatPageReal from "./pages/AIChatPageReal";
import SwirlspacePage from "./pages/SwirlspacePage";
import SmartSwirlInsights from "./pages/SmartSwirlInsights";
import SelfDashboard from "./pages/SelfDashboard";
import Home from "./pages/Home";
import FocusPage from "./pages/FocusPage";
import FocusDetailPage from "./pages/FocusDetailPage";
import AdminEngagementDashboard from "./pages/AdminEngagementDashboard";
import AuthPage from "./pages/AuthPage";
import EmailConfirmation from "./pages/EmailConfirmation";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm";
import PasswordResetHandler from "./pages/PasswordResetHandler";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ExtensionLogin from "./pages/ExtensionLogin";
import SettingsPageNew from "./pages/SettingsPageNew";
import { FLAGS } from "./lib/flags";

// -----------------------------
// FIXED PROTECTED ROUTE
// -----------------------------
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // EXTENSION LOGIN BYPASS
  const params = new URLSearchParams(location.search);
  const extensionId = params.get("extension_id");
  if (extensionId) {
    return children; // Let AuthProvider redirect back to the extension
  }

  // Allow email-confirmation always
  if (location.pathname === "/email-confirmation") {
    return children;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return children;
};

// -----------------------------
// ROUTES
// -----------------------------
const Routes = () => {
  return (
    <BrowserRouter>
      <RouterRoutes>
        <Route path="/email-confirmation" element={<EmailConfirmation />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/password-reset" element={<PasswordResetHandler />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* EXTENSION LOGIN PAGE */}
        <Route path="/extension/login" element={<ExtensionLogin />} />

        {/* MAIN DASHBOARD OR SWIRLSPACE */}
        {FLAGS.SWIRLSPACE_ENABLED ? (
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SwirlspacePage />
              </ProtectedRoute>
            }
          />
        ) : (
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SwirlDashboard />
              </ProtectedRoute>
            }
          />
        )}

        <Route
          path="/channels"
          element={
            <ProtectedRoute>
              <ChannelsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/collections"
          element={
            <ProtectedRoute>
              <CollectionsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/people"
          element={
            <ProtectedRoute>
              <PeoplePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/creative-lab"
          element={
            <ProtectedRoute>
              <CreativeLabPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ai-chat"
          element={
            <ProtectedRoute>
              <AIChatPageReal />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ai-chat/:conversationId"
          element={
            <ProtectedRoute>
              <AIChatPageReal />
            </ProtectedRoute>
          }
        />

        <Route
          path="/focus"
          element={
            <ProtectedRoute>
              <FocusPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/focus/:id"
          element={
            <ProtectedRoute>
              <FocusDetailPage />
            </ProtectedRoute>
          }
        />

        {FLAGS.SWIRLSPACE_ENABLED && (
          <>
            <Route
              path="/swirlspace"
              element={
                <ProtectedRoute>
                  <SwirlspacePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/swirlspace/swirls/:id"
              element={
                <ProtectedRoute>
                  <SmartSwirlInsights />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {FLAGS.SELF_DASHBOARD_ENABLED && (
          <>
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/self"
              element={
                <ProtectedRoute>
                  <SelfDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights"
              element={
                <ProtectedRoute>
                  <SelfDashboard />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {FLAGS.FEATURE_DISCOVER_PAGE && (
          <Route
            path="/discover"
            element={
              <ProtectedRoute>
                <DiscoverPage />
              </ProtectedRoute>
            }
          />
        )}

        {(import.meta.env.VITE_FEATURE_DEBUG_DASHBOARD === "true" || import.meta.env.DEV) && (
          <Route
            path="/debug/data-health"
            element={
              <ProtectedRoute>
                <DataHealthDashboard />
              </ProtectedRoute>
            }
          />
        )}

        <Route
          path="/recommendations"
          element={
            <ProtectedRoute>
              <RecommendationsPage />
            </ProtectedRoute>
          }
        />

        {FLAGS.ENGAGEMENT_DASHBOARD_ENABLED && (
          <Route
            path="/admin/engagement"
            element={
              <ProtectedRoute>
                <AdminEngagementDashboard />
              </ProtectedRoute>
            }
          />
        )}

        {/* SETTINGS ROUTES */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPageNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/:section"
          element={
            <ProtectedRoute>
              <SettingsPageNew />
            </ProtectedRoute>
          }
        />

        {/* FALLBACK */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              {FLAGS.SWIRLSPACE_ENABLED ? <SwirlspacePage /> : <SwirlDashboard />}
            </ProtectedRoute>
          }
        />
      </RouterRoutes>
    </BrowserRouter>
  );
};

export default Routes;